import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Polling cadence — 15 s idle, 5 s active (during consultations).
const POLL_INTERVAL_MS = 15000;

// Local storage key for suppressing repeat browser notifications.
const SEEN_KEY = 'tababa_notified_ids';

const readSeen = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')); }
  catch { return new Set(); }
};
const writeSeen = (set) => {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...set].slice(-200))); }
  catch { /* ignore quota */ }
};

/**
 * useNotifications — headless notification hook.
 *
 * - Polls `/api/notifications` and `/api/notifications/unread-count`.
 * - Fires a native browser Notification for each newly-arrived unread item
 *   (when the tab is hidden or a Notification permission has been granted).
 * - Exposes list/unread/refresh/markRead/markAllRead.
 *
 * The hook is safe to call while unauthenticated — polling silently no-ops
 * on 401 responses.
 */
export function useNotifications({ language = 'ar', enabled = true, activeMode = false } = {}) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const seenRef = useRef(readSeen());

  const fireBrowserNotification = useCallback((n) => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    // Suppress if already shown for this id.
    if (seenRef.current.has(n.id)) return;
    seenRef.current.add(n.id);
    writeSeen(seenRef.current);
    const title = language === 'ar' ? (n.title_ar || n.title_en) : (n.title_en || n.title_ar);
    const body  = language === 'ar' ? (n.message_ar || n.message_en) : (n.message_en || n.message_ar);
    try {
      const notif = new Notification(title || 'Tababa', {
        body,
        tag: n.id,
        icon: '/images/logo.png',
      });
      notif.onclick = () => {
        try { window.focus(); } catch {/* ignore */}
        if (n.link) window.location.href = n.link;
        notif.close();
      };
    } catch {/* browser can throw if not user-visible */}
  }, [language]);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const [listRes, countRes] = await Promise.all([
        axios.get(`${API}/notifications?limit=50`, { withCredentials: true }),
        axios.get(`${API}/notifications/unread-count`, { withCredentials: true }),
      ]);
      const items = listRes.data || [];
      setNotifications(items);
      setUnreadCount(countRes.data?.count || 0);
      // Fire browser notifications for unread items we haven't fired yet.
      items.filter((n) => !n.read).forEach(fireBrowserNotification);
    } catch {/* silent — unauthenticated / offline */}
    finally { setLoading(false); }
  }, [enabled, fireBrowserNotification]);

  const markRead = useCallback(async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch { /* ignore */ }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const interval = activeMode ? 5000 : POLL_INTERVAL_MS;
    const id = setInterval(refresh, interval);
    return () => clearInterval(id);
  }, [enabled, activeMode, refresh]);

  return { notifications, unreadCount, loading, refresh, markRead, markAllRead };
}

/**
 * requestNotificationPermission — one-shot helper to ask the browser for
 * Notification permission. Returns the resulting state ('granted' | 'denied'
 * | 'default' | 'unsupported'). Safe to call multiple times; the browser
 * suppresses repeat prompts after a decision.
 */
export async function requestNotificationPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    const p = await Notification.requestPermission();
    return p;
  } catch {
    return Notification.permission;
  }
}

export function currentNotificationPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}
